import { Language } from './triageTranslations';

export interface ContactTranslations {
  hero: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    teamLink: string;
  };
  form: {
    cardTitle: string;
    cardSubtitle: string;
    fullName: string;
    fullNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    company: string;
    companyPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    inquiryType: string;
    inquiryTypePlaceholder: string;
    inquiryOptions: {
      general: string;
      partnerships: string;
      healthcareProvider: string;
      insurance: string;
      technical: string;
      media: string;
    };
    subject: string;
    subjectPlaceholder: string;
    message: string;
    messagePlaceholder: string;
    sending: string;
    sendMessage: string;
    successTitle: string;
    successDescription: string;
  };
  sidebar: {
    getStarted: string;
    aiAssessment: string;
    aiAssessmentDesc: string;
    contactForm: string;
    contactFormDesc: string;
    responseTime: string;
    generalInquiries: string;
    partnershipInquiries: string;
    technicalSupport: string;
    hours24to48: string;
    days2to3: string;
    within24hours: string;
  };
  cta: {
    title: string;
    subtitle: string;
    scheduleDemo: string;
  };
}

export const contactTranslations: Record<Language, ContactTranslations> = {
  en: {
    hero: {
      title: "Get in",
      titleHighlight: "touch",
      subtitle: "Have questions about CheckFirstCare? Interested in partnerships? Connect with",
      teamLink: "Ranvir Gill",
    },
    form: {
      cardTitle: "Send us a message",
      cardSubtitle: "Fill out the form below and we'll get back to you as soon as possible.",
      fullName: "Full Name *",
      fullNamePlaceholder: "John Doe",
      email: "Email Address *",
      emailPlaceholder: "john@example.com",
      company: "Company/Organization",
      companyPlaceholder: "Your Company",
      phone: "Phone Number",
      phonePlaceholder: "(555) 123-4567",
      inquiryType: "Type of Inquiry",
      inquiryTypePlaceholder: "Select inquiry type",
      inquiryOptions: {
        general: "General Question",
        partnerships: "Partnership Opportunities",
        healthcareProvider: "Healthcare Provider Interest",
        insurance: "Insurance Company Inquiry",
        technical: "Technical Support",
        media: "Media/Press Inquiry",
      },
      subject: "Subject *",
      subjectPlaceholder: "Brief subject line",
      message: "Message *",
      messagePlaceholder: "Tell us more about your inquiry...",
      sending: "Sending...",
      sendMessage: "Send Message",
      successTitle: "Message sent successfully!",
      successDescription: "We'll get back to you within 24 hours.",
    },
    sidebar: {
      getStarted: "Get Started",
      aiAssessment: "AI Assessment",
      aiAssessmentDesc: "Available 24/7 for immediate health guidance",
      contactForm: "Contact Form",
      contactFormDesc: "Use the form to send us your inquiry",
      responseTime: "Response Time",
      generalInquiries: "General Inquiries",
      partnershipInquiries: "Partnership Inquiries",
      technicalSupport: "Technical Support",
      hours24to48: "24-48 hours",
      days2to3: "2-3 business days",
      within24hours: "Within 24 hours",
    },
    cta: {
      title: "Ready to Transform Healthcare Triage?",
      subtitle: "Join us in revolutionizing healthcare decision-making with AI-powered triage technology.",
      scheduleDemo: "Schedule a Demo",
    },
  },
  es: {
    hero: {
      title: "Ponte en",
      titleHighlight: "contacto",
      subtitle: "¿Tienes preguntas sobre CheckFirstCare? ¿Interesado en asociaciones? Conecta con",
      teamLink: "Ranvir Gill",
    },
    form: {
      cardTitle: "Envíanos un mensaje",
      cardSubtitle: "Completa el formulario a continuación y te responderemos lo antes posible.",
      fullName: "Nombre completo *",
      fullNamePlaceholder: "Juan Pérez",
      email: "Correo electrónico *",
      emailPlaceholder: "juan@ejemplo.com",
      company: "Empresa/Organización",
      companyPlaceholder: "Tu empresa",
      phone: "Número de teléfono",
      phonePlaceholder: "(555) 123-4567",
      inquiryType: "Tipo de consulta",
      inquiryTypePlaceholder: "Selecciona tipo de consulta",
      inquiryOptions: {
        general: "Pregunta general",
        partnerships: "Oportunidades de asociación",
        healthcareProvider: "Interés de proveedor de salud",
        insurance: "Consulta de compañía de seguros",
        technical: "Soporte técnico",
        media: "Consulta de medios/prensa",
      },
      subject: "Asunto *",
      subjectPlaceholder: "Línea de asunto breve",
      message: "Mensaje *",
      messagePlaceholder: "Cuéntanos más sobre tu consulta...",
      sending: "Enviando...",
      sendMessage: "Enviar mensaje",
      successTitle: "¡Mensaje enviado con éxito!",
      successDescription: "Te responderemos dentro de 24 horas.",
    },
    sidebar: {
      getStarted: "Comenzar",
      aiAssessment: "Evaluación con IA",
      aiAssessmentDesc: "Disponible 24/7 para orientación de salud inmediata",
      contactForm: "Formulario de contacto",
      contactFormDesc: "Usa el formulario para enviarnos tu consulta",
      responseTime: "Tiempo de respuesta",
      generalInquiries: "Consultas generales",
      partnershipInquiries: "Consultas de asociación",
      technicalSupport: "Soporte técnico",
      hours24to48: "24-48 horas",
      days2to3: "2-3 días hábiles",
      within24hours: "Dentro de 24 horas",
    },
    cta: {
      title: "¿Listo para transformar el triaje de atención médica?",
      subtitle: "Únete a nosotros para revolucionar la toma de decisiones en salud con tecnología de triaje impulsada por IA.",
      scheduleDemo: "Programar una demo",
    },
  },
  hi: {
    hero: {
      title: "संपर्क",
      titleHighlight: "करें",
      subtitle: "CheckFirstCare के बारे में प्रश्न हैं? साझेदारी में रुचि है? जुड़ें",
      teamLink: "रणवीर गिल",
    },
    form: {
      cardTitle: "हमें संदेश भेजें",
      cardSubtitle: "नीचे फॉर्म भरें और हम जल्द से जल्द आपसे संपर्क करेंगे।",
      fullName: "पूरा नाम *",
      fullNamePlaceholder: "जॉन डो",
      email: "ईमेल पता *",
      emailPlaceholder: "john@example.com",
      company: "कंपनी/संगठन",
      companyPlaceholder: "आपकी कंपनी",
      phone: "फ़ोन नंबर",
      phonePlaceholder: "(555) 123-4567",
      inquiryType: "पूछताछ का प्रकार",
      inquiryTypePlaceholder: "पूछताछ का प्रकार चुनें",
      inquiryOptions: {
        general: "सामान्य प्रश्न",
        partnerships: "साझेदारी के अवसर",
        healthcareProvider: "स्वास्थ्य सेवा प्रदाता रुचि",
        insurance: "बीमा कंपनी पूछताछ",
        technical: "तकनीकी सहायता",
        media: "मीडिया/प्रेस पूछताछ",
      },
      subject: "विषय *",
      subjectPlaceholder: "संक्षिप्त विषय पंक्ति",
      message: "संदेश *",
      messagePlaceholder: "अपनी पूछताछ के बारे में और बताएं...",
      sending: "भेजा जा रहा है...",
      sendMessage: "संदेश भेजें",
      successTitle: "संदेश सफलतापूर्वक भेजा गया!",
      successDescription: "हम 24 घंटे के भीतर आपसे संपर्क करेंगे।",
    },
    sidebar: {
      getStarted: "शुरू करें",
      aiAssessment: "AI मूल्यांकन",
      aiAssessmentDesc: "तत्काल स्वास्थ्य मार्गदर्शन के लिए 24/7 उपलब्ध",
      contactForm: "संपर्क फॉर्म",
      contactFormDesc: "अपनी पूछताछ भेजने के लिए फॉर्म का उपयोग करें",
      responseTime: "प्रतिक्रिया समय",
      generalInquiries: "सामान्य पूछताछ",
      partnershipInquiries: "साझेदारी पूछताछ",
      technicalSupport: "तकनीकी सहायता",
      hours24to48: "24-48 घंटे",
      days2to3: "2-3 कार्य दिवस",
      within24hours: "24 घंटे के भीतर",
    },
    cta: {
      title: "स्वास्थ्य देखभाल ट्राइएज को बदलने के लिए तैयार हैं?",
      subtitle: "AI-संचालित ट्राइएज तकनीक के साथ स्वास्थ्य देखभाल निर्णय लेने में क्रांति लाने में हमारे साथ जुड़ें।",
      scheduleDemo: "डेमो शेड्यूल करें",
    },
  },
  pa: {
    hero: {
      title: "ਸੰਪਰਕ",
      titleHighlight: "ਕਰੋ",
      subtitle: "CheckFirstCare ਬਾਰੇ ਸਵਾਲ ਹਨ? ਭਾਈਵਾਲੀ ਵਿੱਚ ਦਿਲਚਸਪੀ ਹੈ? ਜੁੜੋ",
      teamLink: "ਰਣਵੀਰ ਗਿੱਲ",
    },
    form: {
      cardTitle: "ਸਾਨੂੰ ਸੁਨੇਹਾ ਭੇਜੋ",
      cardSubtitle: "ਹੇਠਾਂ ਫਾਰਮ ਭਰੋ ਅਤੇ ਅਸੀਂ ਜਿੰਨੀ ਜਲਦੀ ਹੋ ਸਕੇ ਤੁਹਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰਾਂਗੇ।",
      fullName: "ਪੂਰਾ ਨਾਮ *",
      fullNamePlaceholder: "ਜੌਨ ਡੋ",
      email: "ਈਮੇਲ ਪਤਾ *",
      emailPlaceholder: "john@example.com",
      company: "ਕੰਪਨੀ/ਸੰਸਥਾ",
      companyPlaceholder: "ਤੁਹਾਡੀ ਕੰਪਨੀ",
      phone: "ਫ਼ੋਨ ਨੰਬਰ",
      phonePlaceholder: "(555) 123-4567",
      inquiryType: "ਪੁੱਛਗਿੱਛ ਦੀ ਕਿਸਮ",
      inquiryTypePlaceholder: "ਪੁੱਛਗਿੱਛ ਦੀ ਕਿਸਮ ਚੁਣੋ",
      inquiryOptions: {
        general: "ਆਮ ਸਵਾਲ",
        partnerships: "ਭਾਈਵਾਲੀ ਦੇ ਮੌਕੇ",
        healthcareProvider: "ਸਿਹਤ ਸੇਵਾ ਪ੍ਰਦਾਤਾ ਦਿਲਚਸਪੀ",
        insurance: "ਬੀਮਾ ਕੰਪਨੀ ਪੁੱਛਗਿੱਛ",
        technical: "ਤਕਨੀਕੀ ਸਹਾਇਤਾ",
        media: "ਮੀਡੀਆ/ਪ੍ਰੈਸ ਪੁੱਛਗਿੱਛ",
      },
      subject: "ਵਿਸ਼ਾ *",
      subjectPlaceholder: "ਸੰਖੇਪ ਵਿਸ਼ਾ ਲਾਈਨ",
      message: "ਸੁਨੇਹਾ *",
      messagePlaceholder: "ਆਪਣੀ ਪੁੱਛਗਿੱਛ ਬਾਰੇ ਹੋਰ ਦੱਸੋ...",
      sending: "ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ...",
      sendMessage: "ਸੁਨੇਹਾ ਭੇਜੋ",
      successTitle: "ਸੁਨੇਹਾ ਸਫਲਤਾਪੂਰਵਕ ਭੇਜਿਆ ਗਿਆ!",
      successDescription: "ਅਸੀਂ 24 ਘੰਟਿਆਂ ਦੇ ਅੰਦਰ ਤੁਹਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰਾਂਗੇ।",
    },
    sidebar: {
      getStarted: "ਸ਼ੁਰੂ ਕਰੋ",
      aiAssessment: "AI ਮੁਲਾਂਕਣ",
      aiAssessmentDesc: "ਤੁਰੰਤ ਸਿਹਤ ਮਾਰਗਦਰਸ਼ਨ ਲਈ 24/7 ਉਪਲਬਧ",
      contactForm: "ਸੰਪਰਕ ਫਾਰਮ",
      contactFormDesc: "ਆਪਣੀ ਪੁੱਛਗਿੱਛ ਭੇਜਣ ਲਈ ਫਾਰਮ ਦੀ ਵਰਤੋਂ ਕਰੋ",
      responseTime: "ਜਵਾਬ ਦਾ ਸਮਾਂ",
      generalInquiries: "ਆਮ ਪੁੱਛਗਿੱਛ",
      partnershipInquiries: "ਭਾਈਵਾਲੀ ਪੁੱਛਗਿੱਛ",
      technicalSupport: "ਤਕਨੀਕੀ ਸਹਾਇਤਾ",
      hours24to48: "24-48 ਘੰਟੇ",
      days2to3: "2-3 ਕਾਰੋਬਾਰੀ ਦਿਨ",
      within24hours: "24 ਘੰਟਿਆਂ ਦੇ ਅੰਦਰ",
    },
    cta: {
      title: "ਸਿਹਤ ਦੇਖਭਾਲ ਟ੍ਰਾਈਏਜ ਨੂੰ ਬਦਲਣ ਲਈ ਤਿਆਰ ਹੋ?",
      subtitle: "AI-ਸੰਚਾਲਿਤ ਟ੍ਰਾਈਏਜ ਤਕਨਾਲੋਜੀ ਨਾਲ ਸਿਹਤ ਦੇਖਭਾਲ ਫੈਸਲੇ ਲੈਣ ਵਿੱਚ ਕ੍ਰਾਂਤੀ ਲਿਆਉਣ ਵਿੱਚ ਸਾਡੇ ਨਾਲ ਜੁੜੋ।",
      scheduleDemo: "ਡੈਮੋ ਸ਼ਡਿਊਲ ਕਰੋ",
    },
  },
  bn: {
    hero: {
      title: "যোগাযোগ",
      titleHighlight: "করুন",
      subtitle: "CheckFirstCare সম্পর্কে প্রশ্ন আছে? অংশীদারিত্বে আগ্রহী? যুক্ত হন",
      teamLink: "রণবীর গিল",
    },
    form: {
      cardTitle: "আমাদের একটি বার্তা পাঠান",
      cardSubtitle: "নীচের ফর্মটি পূরণ করুন এবং আমরা যত তাড়াতাড়ি সম্ভব আপনার সাথে যোগাযোগ করব।",
      fullName: "পুরো নাম *",
      fullNamePlaceholder: "জন ডো",
      email: "ইমেল ঠিকানা *",
      emailPlaceholder: "john@example.com",
      company: "কোম্পানি/সংস্থা",
      companyPlaceholder: "আপনার কোম্পানি",
      phone: "ফোন নম্বর",
      phonePlaceholder: "(555) 123-4567",
      inquiryType: "অনুসন্ধানের ধরন",
      inquiryTypePlaceholder: "অনুসন্ধানের ধরন নির্বাচন করুন",
      inquiryOptions: {
        general: "সাধারণ প্রশ্ন",
        partnerships: "অংশীদারিত্বের সুযোগ",
        healthcareProvider: "স্বাস্থ্যসেবা প্রদানকারী আগ্রহ",
        insurance: "বীমা কোম্পানি অনুসন্ধান",
        technical: "প্রযুক্তিগত সহায়তা",
        media: "মিডিয়া/প্রেস অনুসন্ধান",
      },
      subject: "বিষয় *",
      subjectPlaceholder: "সংক্ষিপ্ত বিষয় লাইন",
      message: "বার্তা *",
      messagePlaceholder: "আপনার অনুসন্ধান সম্পর্কে আরও বলুন...",
      sending: "পাঠানো হচ্ছে...",
      sendMessage: "বার্তা পাঠান",
      successTitle: "বার্তা সফলভাবে পাঠানো হয়েছে!",
      successDescription: "আমরা 24 ঘন্টার মধ্যে আপনার সাথে যোগাযোগ করব।",
    },
    sidebar: {
      getStarted: "শুরু করুন",
      aiAssessment: "AI মূল্যায়ন",
      aiAssessmentDesc: "তাৎক্ষণিক স্বাস্থ্য নির্দেশনার জন্য 24/7 উপলব্ধ",
      contactForm: "যোগাযোগ ফর্ম",
      contactFormDesc: "আপনার অনুসন্ধান পাঠাতে ফর্ম ব্যবহার করুন",
      responseTime: "প্রতিক্রিয়া সময়",
      generalInquiries: "সাধারণ অনুসন্ধান",
      partnershipInquiries: "অংশীদারিত্ব অনুসন্ধান",
      technicalSupport: "প্রযুক্তিগত সহায়তা",
      hours24to48: "24-48 ঘন্টা",
      days2to3: "2-3 কার্যদিবস",
      within24hours: "24 ঘন্টার মধ্যে",
    },
    cta: {
      title: "স্বাস্থ্যসেবা ট্রায়াজ রূপান্তর করতে প্রস্তুত?",
      subtitle: "AI-চালিত ট্রায়াজ প্রযুক্তির সাথে স্বাস্থ্যসেবা সিদ্ধান্ত গ্রহণে বিপ্লব আনতে আমাদের সাথে যোগ দিন।",
      scheduleDemo: "ডেমো সময়সূচী করুন",
    },
  },
};
